/**
 * @file SimilarPlayersSortDropdownContainer — desktop sort selector.
 * @description SE-5570: the desktop sort dropdown for the similar-players
 * filter panel, migrated off the retired gluestack Menu atom onto the shared
 * `SelectDropdown` molecule (FixedPositionDropdownShell). Owns its own open
 * state; the shell hosts the panel in a native Modal (web portals to the
 * document body). The mobile branch keeps the inline `SortOptionsList` — this
 * molecule is the desktop replacement only. The `SP_SORT_DROPDOWN` trigger and
 * `SP_SORT_OPTION_PREFIX` option testIDs are preserved for e2e.
 * @module features/player-detail/components/molecules/SimilarPlayersSortDropdown
 */
import { ChevronDown } from "lucide-react-native";
import React, { memo, useCallback, useState } from "react";

import { Icon } from "@/components/atoms/Icon";
import { Stack } from "@/components/atoms/Stack";
import { Text } from "@/components/atoms/Text";
import SelectDropdown, {
  type SelectDropdownOption,
} from "@/components/molecules/SelectDropdown";
import type {
  SimilarPlayersSortOption,
  SortFieldCallbackMap,
} from "@/features/player-detail/types";
import type { SimilarPlayerSortField } from "@/generated/graphql";
import { TEST_ID_PLAYER_DETAIL } from "e2e/testId";

/** Props for {@link SimilarPlayersSortDropdownContainer}. */
export interface SimilarPlayersSortDropdownProps {
  /** Whether dark mode is active */
  readonly isDark: boolean;
  /** Available sort options */
  readonly sortOptions: readonly SimilarPlayersSortOption[];
  /** Currently selected sort field */
  readonly selectedSortField: SimilarPlayerSortField;
  /** Display label for the selected sort option (trigger text) */
  readonly selectedSortLabel: string;
  /** Pre-bound callbacks for each sort field */
  readonly sortFieldCallbacks: SortFieldCallbackMap;
}

/**
 * Desktop sort dropdown rendered through the shared SelectDropdown molecule.
 * @param props - Component props
 * @param props.isDark - Whether dark mode is active
 * @param props.sortOptions - Available sort options
 * @param props.selectedSortField - Currently selected sort field
 * @param props.selectedSortLabel - Display label for the selected sort option
 * @param props.sortFieldCallbacks - Pre-bound callbacks for each sort field
 * @returns The sort SelectDropdown element
 */
const SimilarPlayersSortDropdownContainer = ({
  sortOptions,
  selectedSortField,
  selectedSortLabel,
  sortFieldCallbacks,
}: SimilarPlayersSortDropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const onToggle = useCallback(() => setIsOpen(prev => !prev), []);
  const onClose = useCallback(() => setIsOpen(false), []);

  const options: SelectDropdownOption[] = sortOptions.map(option => ({
    id: option.id,
    label: option.label,
    selected: selectedSortField === option.field,
    onPress: sortFieldCallbacks[option.field],
    testID: `${TEST_ID_PLAYER_DETAIL.SP_SORT_OPTION_PREFIX}${option.field}`,
  }));

  return (
    <SelectDropdown
      isOpen={isOpen}
      onToggle={onToggle}
      onClose={onClose}
      triggerTestID={TEST_ID_PLAYER_DETAIL.SP_SORT_DROPDOWN}
      triggerAccessibilityLabel="Sort similar players"
      triggerClassName="h-10"
      triggerContent={
        <Stack
          direction="horizontal"
          className="h-full flex-1 items-center justify-between rounded-none border border-outline-strong bg-surface-raised px-3"
        >
          <Text
            variant="unstyled"
            className="text-title-sm text-content-primary"
          >
            {selectedSortLabel}
          </Text>
          <Icon as={ChevronDown} size="sm" className="text-content-primary" />
        </Stack>
      }
      options={options}
    />
  );
};

SimilarPlayersSortDropdownContainer.displayName =
  "SimilarPlayersSortDropdownContainer";

export default memo(SimilarPlayersSortDropdownContainer);
